import { Children, type ReactNode } from 'react';
import { Box, Text } from '@chakra-ui/react';

interface NotificationSectionProps {
  label: string;
  /** Rows sit directly on a white surface (the bell panel) instead of in their own card. */
  flush?: boolean;
  /** Every row in it is being deleted: the section closes up along with them. */
  collapsing?: boolean;
  children: ReactNode;
}

/** A time group: an uppercase label over its rows, split by inset dividers. */
export function NotificationSection({
  label,
  flush = false,
  collapsing = false,
  children,
}: NotificationSectionProps) {
  return (
    // Same 1fr -> 0fr collapse and timing as a deleted row (NotificationRow.module.css).
    <Box
      as="section"
      aria-label={label}
      aria-hidden={collapsing || undefined}
      display="grid"
      gridTemplateRows={collapsing ? '0fr' : '1fr'}
      transition="grid-template-rows 220ms cubic-bezier(0.4, 0, 0.2, 1) 160ms"
    >
      {/* Clip only while closing; at rest it would cut off the card's shadow. */}
      <Box minH="0" overflow={collapsing ? 'hidden' : 'visible'}>
        {/* The page's gap between sections lives in here so it closes up too. Padding on
            the collapsing box itself would hold it open (border-box can't shrink past it). */}
        <Box pb={flush ? 0 : 5}>
          <Text
            as="h2"
            m="0"
            mb={flush ? 1 : 2}
            pl={flush ? 4 : 1}
            pt={flush ? 3 : 0}
            fontSize="xs"
            lineHeight="16px"
            fontWeight="semibold"
            letterSpacing="0.04em"
            textTransform="uppercase"
            color="volyx.textSecondary"
          >
            {label}
          </Text>
          <Box
            as="ul"
            m="0"
            p="0"
            listStyleType="none"
            bg="white"
            borderRadius={flush ? undefined : '2xl'}
            overflow="hidden"
            boxShadow={flush ? undefined : '0 1px 3px rgba(31, 41, 50, 0.06)'}
          >
            {Children.map(children, (child, i) => (
              <Box
                as="li"
                position="relative"
                _before={
                  i > 0
                    ? {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: '68px',
                        right: 0,
                        height: '1px',
                        bg: 'volyx.borderLight',
                        zIndex: 1,
                      }
                    : undefined
                }
              >
                {child}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
